import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import type { TRPCRouter } from '@tempo-io/notification-app/trpc';
import { createTRPCClient, httpBatchLink, httpSubscriptionLink, splitLink } from '@trpc/client';
import { createTRPCContext } from '@trpc/tanstack-react-query';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { useState } from 'react';
import SuperJSON from 'superjson';
import { getEnv } from '@/lib/utils/common/getEnv';

const trpcContext = createTRPCContext<TRPCRouter>();

function useTRPCClient() {
  const { getAccessTokenSilently } = useAuth0();
  const env = getEnv();
  const url = `${env.apiBaseUrl}/api/sse/v1/trpc`;
  const [client] = useState(() =>
    createTRPCClient<TRPCRouter>({
      links: [
        splitLink({
          condition: (op) => op.type === 'subscription',
          true: httpSubscriptionLink({
            url,
            transformer: SuperJSON,
            EventSource: EventSourcePolyfill,
            eventSourceOptions: async () => {
              const token = await getAccessTokenSilently();
              return {
                headers: {
                  authorization: `Bearer ${token}`,
                },
                heartbeatTimeout: 120_000,
              };
            },
          }),
          false: httpBatchLink({
            url,
            transformer: SuperJSON,
            async headers() {
              const token = await getAccessTokenSilently();
              return {
                authorization: `Bearer ${token}`,
              };
            },
          }),
        }),
      ],
    }),
  );

  return client;
}

export const TRPCProvider = ({ children }: { children: React.ReactNode }) => {
  const trpcClient = useTRPCClient();
  const queryClient = useQueryClient();
  return (
    <trpcContext.TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
      {children}
    </trpcContext.TRPCProvider>
  );
};

export const { useTRPC } = trpcContext;
